/* ──────────────────────────────────────────────────────────────
 *  SearchBar — Magnify icon that expands into a TextField
 *
 *  Enter-key triggers search: canonicalizes input, finds sample
 *  ID in allSamples, and selects it — driving the geometry
 *  loading overlay via useSampleSelection when a /mesh fetch is
 *  actually needed.
 * ────────────────────────────────────────────────────────────── */

import React, { useState, useRef, useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Collapse from '@mui/material/Collapse';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { useSampleStore } from '@/stores/useSampleStore';
import { useSampleSelection } from '@/hooks/useSampleSelection';

const SearchBar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const allSamples = useSampleStore((s) => s.allSamples);
  const { selectSampleWithGeometry } = useSampleSelection();

  const handleToggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev) {
        setTimeout(() => inputRef.current?.focus(), 150);
      } else {
        setValue('');
        setError(null);
      }
      return !prev;
    });
  }, []);

  const handleSearch = useCallback(() => {
    const raw = value.trim();
    if (!raw) {
      setError(null);
      void selectSampleWithGeometry(null);
      return;
    }

    const candidates = [
      raw,
      raw.toUpperCase(),
      `N${raw.replace(/^N/i, '').padStart(4, '0')}`,
    ];

    const found = allSamples.find((row) => {
      const id = String(row.id ?? '');
      return candidates.includes(id);
    });

    if (found) {
      const id = String(found.id);
      setValue(id);
      setError(null);
      void selectSampleWithGeometry(id);
    } else {
      setError(`'${raw}' not found`);
    }
  }, [value, allSamples, selectSampleWithGeometry]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  return (
    <>
      <Collapse in={open} orientation="horizontal" collapsedSize={0}>
        <TextField
          inputRef={inputRef}
          size="small"
          placeholder="Sample ID…"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          error={!!error}
          helperText={error}
          sx={{ width: 160 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleToggle}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Collapse>
      {!open && (
        <IconButton size="small" onClick={handleToggle} title="Search sample">
          <SearchIcon fontSize="small" />
        </IconButton>
      )}
    </>
  );
};

export default React.memo(SearchBar);